import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function listFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.offsetParent !== null,
  );
}

/** Focus trap + Escape + restore focus for modal dialogs. */
export function useFocusTrap(
  active: boolean,
  onClose?: () => void,
  options?: { initialFocus?: 'first' | 'last' | 'container' },
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const initialFocus = options?.initialFocus ?? 'container';

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!active) return;
    previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const node = containerRef.current;
    if (!node) return;

    const focusInitial = () => {
      // Prefer the dialog container so parent re-renders / scrollable lists are not yanked
      // to the first link every time dependencies change.
      if (initialFocus === 'container') {
        node.focus();
        return;
      }
      const items = listFocusable(node);
      const target = initialFocus === 'last' ? items[items.length - 1] : items[0];
      (target || node).focus();
    };
    const frame = window.requestAnimationFrame(focusInitial);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = listFocusable(node);
      if (items.length === 0) {
        event.preventDefault();
        node.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      previousFocus.current?.focus();
    };
    // Only re-run when the dialog opens/closes — not when parent re-creates onClose.
  }, [active, initialFocus]);

  return containerRef;
}
